import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { login } from 'src/store/actions/accountActions';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const useStyles = makeStyles(() => ({
  root: {},
}));
function LoginForm({ className, onSubmitSuccess, ...rest }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Formik
      initialValues={{
        email: '',
        password: '',
      }}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await dispatch(login(values.email, values.password));
          enqueueSnackbar(t('Welcome'), {
            variant: 'success',
          });
          onSubmitSuccess();
        } catch (error) {
          const responseError = error?.response?.data.message
            ?? error?.response?.data
            ?? error.message;
          enqueueSnackbar(t(responseError), {
            variant: 'error',
          });
          setStatus({ success: false });
          setErrors({ submit: error.message, message: responseError });
          setSubmitting(false);
        }
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .email('Must be a valid email')
          .max(255)
          .required(t('Email is required')),
        password: Yup.string().max(255).required(t('Password is required')),
      })}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <form
          className={clsx(classes.root, className)}
          noValidate
          onSubmit={handleSubmit}
          {...rest}
        >
          <TextField
            autoFocus
            error={Boolean(touched.email && errors.email)}
            fullWidth
            helperText={touched.email && errors.email}
            margin="normal"
            name="email"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={t('email')}
            type="email"
            value={values.email}
            variant="outlined"
          />
          <TextField
            error={Boolean(touched.password && errors.password)}
            fullWidth
            helperText={touched.password && errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    edge="end"
                    onClick={handleClickShowPassword}
                    size="large"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            margin="normal"
            name="password"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={t('Password')}
            type={showPassword ? 'text' : 'password'}
            value={values.password}
            variant="outlined"
          />
          {!isSubmitting ? (
            <Box mt={2}>
              <Button
                autoFocus
                color="secondary"
                disabled={isSubmitting}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                {t('Log in')}
              </Button>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          )}
          {errors.submit && (
            <Box mt={3}>
              <FormHelperText error>{t(errors.message)}</FormHelperText>
            </Box>
          )}
        </form>
      )}
    </Formik>
  );
}

LoginForm.propTypes = {
  className: PropTypes.string,
  onSubmitSuccess: PropTypes.func,
};

LoginForm.defaultProps = {
  onSubmitSuccess: () => {},
};

export default LoginForm;
